/**
 * Landing Page
 * Minimal landing page showing version information
 */

import { VERSION } from "../shared/version.generated";
import { getPublicConfig } from "../excel/config/getPublicConfig";

interface BackendVersion {
  version: {
    appName: string;
    env: "dev" | "prod";
    gitSha: string;
    buildTime: string;
  };
}

/**
 * Format ISO timestamp for display
 */
function formatTime(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Fetch backend version
 */
async function fetchBackendVersion(apiBaseUrl: string): Promise<BackendVersion | null> {
  try {
    const url = apiBaseUrl.replace(/\/$/, "") + "/version";
    const response = await fetch(url);
    if (!response.ok) {
      console.log("Backend version fetch failed: " + response.status);
      return null;
    }
    return (await response.json()) as BackendVersion;
  } catch (error) {
    console.log("Backend version fetch error: " + (error instanceof Error ? error.message : String(error)));
    return null;
  }
}

/**
 * Update frontend version display
 */
function updateFrontendVersion(): void {
  const gitShaEl = document.getElementById("frontend-gitsha");
  const buildTimeEl = document.getElementById("frontend-buildtime");
  const envEl = document.getElementById("frontend-env");

  if (gitShaEl) {
    gitShaEl.textContent = VERSION.gitSha;
  }
  if (buildTimeEl) {
    buildTimeEl.textContent = formatTime(VERSION.buildTime);
  }
  if (envEl) {
    envEl.textContent = VERSION.env;
  }
}

/**
 * Update backend version display
 */
function updateBackendVersion(backendVersion: BackendVersion | null): void {
  const gitShaEl = document.getElementById("backend-gitsha");
  const buildTimeEl = document.getElementById("backend-buildtime");
  const envEl = document.getElementById("backend-env");

  if (!backendVersion) {
    if (gitShaEl) {
      gitShaEl.textContent = "Error loading";
      gitShaEl.className = "";
    }
    if (buildTimeEl) {
      buildTimeEl.textContent = "Error loading";
      buildTimeEl.className = "";
    }
    if (envEl) {
      envEl.textContent = "Error loading";
      envEl.className = "";
    }
    return;
  }

  const v = backendVersion.version;
  if (gitShaEl) {
    gitShaEl.textContent = v.gitSha;
    gitShaEl.className = "";
  }
  if (buildTimeEl) {
    buildTimeEl.textContent = formatTime(v.buildTime);
    buildTimeEl.className = "";
  }
  if (envEl) {
    envEl.textContent = v.env;
    envEl.className = "";
  }

  // Check for version mismatch
  const mismatchEl = document.getElementById("version-mismatch");
  if (mismatchEl && v.gitSha !== VERSION.gitSha) {
    mismatchEl.classList.remove("warning-hidden");
  } else if (mismatchEl) {
    mismatchEl.classList.add("warning-hidden");
  }
}

/**
 * Update API base URL display
 */
function updateApiBaseUrl(apiBaseUrl: string): void {
  const apiBaseUrlEl = document.getElementById("api-base-url");
  if (apiBaseUrlEl) {
    apiBaseUrlEl.textContent = apiBaseUrl;
  }
}

/**
 * Update taskpane link with cache-bust query param
 */
function updateTaskpaneLink(): void {
  const taskpaneLink = document.getElementById("taskpane-link") as HTMLAnchorElement;
  if (taskpaneLink) {
    // Add cache-bust query param using git SHA
    const cacheBust = encodeURIComponent(VERSION.gitSha);
    taskpaneLink.href = "/taskpane.html?v=" + cacheBust;
  }
}

/**
 * If we landed on index (or wrong page) with OAuth callback params,
 * redirect to the auth callback handler. Supabase may redirect to Site URL (root) instead of redirect_to.
 * Also handles hash params (some flows use #code=...).
 * Returns true if redirect was initiated.
 */
function redirectOAuthCallbackIfNeeded(): boolean {
  const search = window.location.search;
  const hash = window.location.hash ? window.location.hash.slice(1) : "";
  const params = new URLSearchParams(search || (hash ? "?" + hash : ""));
  const code = params.get("code");
  const state = params.get("state");
  const pathname = window.location.pathname || "/";
  const isRoot = pathname === "" || pathname === "/" || pathname === "/index.html";
  if (code && state && isRoot) {
    const queryString = search || (hash ? "?" + hash : "");
    const callbackUrl = "/auth/callback.html" + queryString;
    window.location.replace(callbackUrl);
    return true;
  }
  return false;
}

/**
 * Initialize landing page
 */
async function initialize(): Promise<void> {
  if (redirectOAuthCallbackIfNeeded()) return;

  // Update frontend version immediately
  updateFrontendVersion();
  
  // Update taskpane link with cache-bust param
  updateTaskpaneLink();

  // Load config and fetch backend version
  try {
    const config = await getPublicConfig();
    const apiBaseUrl = config.public.apiBaseUrl;
    updateApiBaseUrl(apiBaseUrl);

    const backendVersion = await fetchBackendVersion(apiBaseUrl);
    updateBackendVersion(backendVersion);
  } catch (error) {
    console.log("Failed to initialize landing page: " + (error instanceof Error ? error.message : String(error)));
    updateBackendVersion(null);
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    void initialize();
  });
} else {
  void initialize();
}
